/**
 * Secure storage interface for bridge credentials
 */

export interface SecureStorageInterface {
  setItem(key: string, value: unknown): Promise<void>
  getItem(key: string): Promise<unknown | null>
  removeItem(key: string): Promise<void>
  clear(): Promise<void>
}

/**
 * Hue Bridge Credentials for API v2
 * Note: 'username' is the v1 term - it's used as 'hue-application-key' in v2 API
 */
export interface HueBridgeCredentials {
  bridgeId: string
  username: string // Used as 'hue-application-key' header in v2 API
  clientKey: string // Used for Entertainment API
  applicationId?: string // hue-application-id from GET /auth/v1 (for Entertainment v2)
  lastConnected: number
  bridgeInfo?: {
    name: string
    ip: string
    modelid: string
    swversion: string
  }
  // v2 API resource mappings (v1 light ID -> v2 UUID)
  resourceIdMap?: {
    [v1LightId: string]: string // e.g. "1" -> "c6b028c8-076e-4817-92b1-bcb0cbb78783"
  }
  lastV2Sync?: number // Timestamp of last resource mapping fetch
}

/**
 * Base encryption utilities
 */
export class EncryptionUtils {
  private static async generateKey(): Promise<CryptoKey> {
    return await crypto.subtle.generateKey(
      {
        name: 'AES-GCM',
        length: 256,
      },
      true,
      ['encrypt', 'decrypt']
    )
  }

  private static async exportKey(key: CryptoKey): Promise<string> {
    const exported = await crypto.subtle.exportKey('raw', key)
    return Array.from(new Uint8Array(exported))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
  }

  private static async importKey(keyHex: string): Promise<CryptoKey> {
    const keyData = new Uint8Array(
      keyHex.match(/.{2}/g)!.map(byte => parseInt(byte, 16))
    )
    return await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'AES-GCM' },
      true,
      ['encrypt', 'decrypt']
    )
  }

  static async getOrCreateEncryptionKey(): Promise<CryptoKey> {
    const stored = localStorage.getItem('hue_encryption_key')
    if (stored) {
      try {
        return await this.importKey(stored)
      } catch {
        // Key corrupted, create new one
      }
    }

    const newKey = await this.generateKey()
    const exported = await this.exportKey(newKey)
    localStorage.setItem('hue_encryption_key', exported)
    return newKey
  }

  static async encrypt(data: string, key: CryptoKey): Promise<string> {
    const encoded = new TextEncoder().encode(data)
    const iv = crypto.getRandomValues(new Uint8Array(12))
    
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encoded
    )

    const combined = new Uint8Array(iv.length + encrypted.byteLength)
    combined.set(iv)
    combined.set(new Uint8Array(encrypted), iv.length)

    return Array.from(combined)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
  }

  static async decrypt(encryptedHex: string, key: CryptoKey): Promise<string> {
    const encrypted = new Uint8Array(
      encryptedHex.match(/.{2}/g)!.map(byte => parseInt(byte, 16))
    )

    const iv = encrypted.slice(0, 12)
    const data = encrypted.slice(12)

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      data
    )

    return new TextDecoder().decode(decrypted)
  }
}